import React, { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Location } from 'history'
import queryString, { ParsedQuery } from 'query-string'
import Dropdown from 'react-bootstrap/Dropdown'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import isFunction from 'lodash/isFunction'
import { PERMISSIONS } from 'src/config/roleConfig'
import { NONE_ID } from 'src/Utils/globalConstants'
import { useNavigateContext } from '../../../Navigation/NavigateContext'
import { CheckBoxInput } from '../../../Inputs/CheckBox'
import { CountChip } from '../../../Chips/BasicChip'
import { FilterName, FilterParam } from '../FilterBar.types'
import {
  FilterDropdownItem,
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from '../FilterBar.styles'
import { DropdownItemType } from './FilterDropdown.constants'
import {
  DropDownMenuWithNesting,
  FilterOptionContainer,
  FilterDropdownIcon,
} from './FilterDropdown.styles'
import { NestedFilter, NestedFilterChevron } from './FilterDropdown.components'

type FilterDropdownBaseProps = {
  filterParam: FilterParam
  filterName: FilterName
  filterValues: DropdownItemType[]
  isLoading?: boolean
  hasNested?: boolean
  includeParentFilter?: boolean
  isNested?: boolean
  isSingleSelect?: boolean
  icon?: React.ReactNode
}

type FilterDropdownConditionalProps =
  | {
      show: boolean
      handleToggle: (nextShow: boolean) => void
    }
  | {
      show?: undefined
      handleToggle?: undefined
    }

type FilterDropdownProps = FilterDropdownBaseProps &
  FilterDropdownConditionalProps

export const FilterDropdown = React.memo(
  ({
    filterParam,
    filterName,
    filterValues,
    isLoading,
    show,
    handleToggle,
    hasNested,
    includeParentFilter,
    isNested,
    isSingleSelect = false,
    icon,
  }: FilterDropdownProps): JSX.Element => {
    const [activeSubmenu, setActiveSubmenu] = useState<string>(NONE_ID)
    const [isOpen, setIsOpen] = useState(false)
    const [internalMultiOpen, setInternalMultiOpen] = useState(false)
    const isMultiExternallyControlled = handleToggle !== undefined
    const location = useLocation()
    const searchParamsFallback = useMemo(() => {
      return queryString.parse(location.search, {
        arrayFormat: 'bracket',
      })
    }, [location])
    const {
      navigateState: {
        parsedQueryParams,
        locationSearchString: contextLocationSearchString,
      },
    } = useNavigateContext()
    const searchParams = parsedQueryParams || searchParamsFallback
    const locationSearchString = contextLocationSearchString ?? location.search

    // For single-select, we need to handle the value differently
    const appliedFilters: Record<string, string[] | string> = useMemo(() => {
      if (isSingleSelect) {
        const appliedFilters: Record<string, string> = {}
        const preprocessed = searchParams[filterParam]
        if (Array.isArray(preprocessed)) {
          // If it's an array, take the first value (shouldn't happen but handle it)
          appliedFilters[filterParam] = preprocessed[0] || ''
        } else {
          appliedFilters[filterParam] = (preprocessed as string) || ''
        }
        return appliedFilters
      } else {
        const appliedFilters: Record<string, string[]> = {}
        const preprocessed = searchParams[filterParam]
        if (Array.isArray(preprocessed)) {
          appliedFilters[filterParam] = preprocessed
        } else {
          appliedFilters[filterParam] = preprocessed ? [preprocessed] : []
        }
        filterValues.forEach((value) => {
          if (
            value.nestedFilter &&
            searchParams[value.nestedFilter.filterParam]
          ) {
            if (Array.isArray(searchParams[value.nestedFilter.filterParam])) {
              appliedFilters[value.nestedFilter.filterParam] = searchParams[
                value.nestedFilter.filterParam
              ] as string[]
            } else {
              appliedFilters[filterParam] = [
                searchParams[value.nestedFilter.filterParam] as string,
              ]
            }
          }
        })
        return appliedFilters
      }
    }, [searchParams, filterParam, filterValues, isSingleSelect])

    const isSmartCheckFilter = filterParam === FilterParam.AI_CONTROL_CHECKS

    // For single-select, get the selected value's label
    const selectedValueLabel = useMemo(() => {
      if (!isSingleSelect) return null
      const selectedValue = appliedFilters[filterParam] as string
      if (!selectedValue) return null
      const selectedItem = filterValues.find(
        (item) => item.value === selectedValue,
      )
      return selectedItem?.label || null
    }, [isSingleSelect, appliedFilters, filterParam, filterValues])

    // Determine if there's a selection
    const hasSelection = isSingleSelect
      ? !!(appliedFilters[filterParam] as string)
      : Object.values(appliedFilters).flat().length > 0

    // Single-select: internal open state. Multi-select: parent-controlled or internal
    // so react-bootstrap always receives an explicit `show` and root-close works reliably.
    const multiSelectShow = isMultiExternallyControlled
      ? show
      : internalMultiOpen
    const dropdownShow = isSingleSelect ? isOpen : multiSelectShow

    const dropdownOnToggle = useCallback(
      (nextShow: boolean) => {
        setActiveSubmenu(NONE_ID)
        if (isSingleSelect) {
          setIsOpen(nextShow)
        } else if (isMultiExternallyControlled) {
          handleToggle(nextShow)
        } else {
          setInternalMultiOpen(nextShow)
        }
      },
      [isSingleSelect, isMultiExternallyControlled, handleToggle],
    )

    return (
      <Dropdown
        autoClose={isSingleSelect ? true : 'outside'}
        show={dropdownShow}
        onToggle={dropdownOnToggle}
      >
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          $isAi={isSmartCheckFilter}
          id={`${filterName}-dropdown`}
          $hasSelection={hasSelection}
          $isNested={isNested}
        >
          <FilterDropdownToggleContent>
            {icon && <FilterDropdownIcon>{icon}</FilterDropdownIcon>}
            {isSingleSelect && selectedValueLabel ? (
              <p>
                {filterName}: {selectedValueLabel}
              </p>
            ) : (
              <>
                <p>{filterName}</p>
                {!isSingleSelect &&
                  Object.values(appliedFilters).flat().length > 0 && (
                    <CountChip ml="xxs">
                      {Object.values(appliedFilters).flat().length}
                    </CountChip>
                  )}
              </>
            )}
          </FilterDropdownToggleContent>
        </Dropdown.Toggle>
        {isLoading ? (
          <Dropdown.Menu rootCloseEvent="mousedown">
            <MenuPlaceholder height={108} />
          </Dropdown.Menu>
        ) : (
          <DropDownMenuWithNesting $hasNested={hasNested}>
            {isSingleSelect ? (
              <SingleSelectDropdownItems
                filterParam={filterParam}
                filterValues={filterValues}
                location={location}
                searchParams={searchParams}
                appliedFilters={appliedFilters as Record<string, string>}
                onSelect={() => isSingleSelect && setIsOpen(false)}
              />
            ) : (
              <MultiSelectDropdownItems
                filterParam={filterParam}
                filterValues={filterValues}
                location={location}
                searchParams={searchParams}
                appliedFilters={appliedFilters as Record<string, string[]>}
                activeSubmenu={activeSubmenu}
                setActiveSubmenu={setActiveSubmenu}
                locationSearchString={locationSearchString}
                includeParentFilter={includeParentFilter}
              />
            )}
          </DropDownMenuWithNesting>
        )}
      </Dropdown>
    )
  },
)

export const SimpleDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  replaceParam,
}: {
  filterParam: FilterParam
  filterValue: DropdownItemType
  appliedFilters: Record<string, string[]>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  replaceParam?: boolean
}): JSX.Element => {
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const newAppliedFilters = new Set(appliedFilters[filterParam]?.map((f) => f))
  const isCustom = isFunction(filterValue.customOnClick)
  const isNestedFilterApplied =
    (filterValue.nestedFilter &&
      appliedFilters[filterValue.nestedFilter.filterParam]?.length > 0) ||
    false
  const isChecked =
    newAppliedFilters.has(filterValue.value) || isNestedFilterApplied
  isChecked
    ? newAppliedFilters.delete(filterValue.value)
    : newAppliedFilters.add(filterValue.value)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        [filterParam]: replaceParam
          ? [filterValue.value]
          : Array.from(newAppliedFilters),
      },
    },
    { arrayFormat: 'bracket' },
  )

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        to={href}
        onClick={async (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(href, { replace: true })
          }
        }}
      >
        {filterValue.label}
      </FilterDropdownItem>
    </FilterOptionContainer>
  )
}

export const SimpleDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  replaceParam,
}: {
  filterParam: FilterParam
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string[]>
  replaceParam?: boolean
}): JSX.Element => (
  <>
    {filterValues.map((filterValue) => (
      <SimpleDropdownItemComponent
        key={filterValue.value}
        filterParam={filterParam}
        filterValue={filterValue}
        appliedFilters={appliedFilters}
        baseQuery={searchParams}
        location={location}
        replaceParam={replaceParam}
      />
    ))}
  </>
)

export const MultiSelectDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  activeSubmenu,
  setActiveSubmenu,
  locationSearchString,
  includeParentFilter,
}: {
  filterParam: FilterParam
  filterValue: DropdownItemType
  appliedFilters: Record<string, string[]>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  activeSubmenu: string
  setActiveSubmenu: (value: string) => void
  locationSearchString: string
  includeParentFilter?: boolean
}): JSX.Element => {
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const newAppliedFilters = new Set(appliedFilters[filterParam]?.map((f) => f))
  const isCustom = isFunction(filterValue.customOnClick)
  const isNestedFilterApplied =
    (filterValue.nestedFilter &&
      appliedFilters[filterValue.nestedFilter.filterParam]?.length > 0) ||
    false
  const isChecked =
    newAppliedFilters.has(filterValue.value) || isNestedFilterApplied
  isChecked
    ? newAppliedFilters.delete(filterValue.value)
    : newAppliedFilters.add(filterValue.value)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        [filterParam]: Array.from(newAppliedFilters),
      },
    },
    { arrayFormat: 'bracket' },
  )

  const nestedFilterHasValues =
    filterValue.nestedFilter && !!filterValue.nestedFilter.filterValues?.length

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        to={href}
        onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (filterValue.nestedFilter && nestedFilterHasValues) {
            if (activeSubmenu != filterValue.nestedFilter.filterParam) {
              setActiveSubmenu(filterValue.nestedFilter.filterParam)
            } else {
              setActiveSubmenu(NONE_ID)
            }
            return
          }
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(href, { replace: true })
          }
        }}
      >
        <CheckBoxInput
          id={filterValue.value}
          label={filterValue.label as React.ReactNode}
          checked={isChecked}
          onClick={(e) => {
            e.stopPropagation()
            if (isCustom) {
              filterValue.customOnClick?.()
              return
            }
          }}
          onChange={async (e) => {
            if (isCustom) {
              e.preventDefault()
              return
            }
            if (filterValue.nestedFilter) {
              setActiveSubmenu(NONE_ID)
              const selectAllHref = queryString.stringifyUrl(
                {
                  url: location.pathname,
                  query: {
                    ...baseQuery,
                    [filterValue.nestedFilter.filterParam]: Array.from(
                      filterValue.nestedFilter?.filterValues
                        .map((f) => f.value)
                        .filter((f) => f !== 'custom') || [],
                    ),
                    ...(includeParentFilter && {
                      [filterParam]: [
                        ...(appliedFilters[filterParam] || []),
                        filterValue.value,
                      ].filter(
                        (value, index, array) => array.indexOf(value) === index,
                      ),
                    }),
                  },
                },
                { arrayFormat: 'bracket' },
              )
              const deselectAllHref = includeParentFilter
                ? queryString.stringifyUrl(
                    {
                      url: location.pathname,
                      query: {
                        ...baseQuery,
                        [filterValue.nestedFilter.filterParam]: [],
                        [filterParam]:
                          appliedFilters[filterParam]?.filter(
                            (f) => f !== filterValue.value,
                          ) || [],
                      },
                    },
                    { arrayFormat: 'bracket' },
                  )
                : queryString.exclude(
                    `${location.pathname}${locationSearchString}`,
                    [filterValue.nestedFilter.filterParam],
                    {
                      arrayFormat: 'bracket',
                    },
                  )
              const selectSelfHref = queryString.stringifyUrl(
                {
                  url: location.pathname,
                  query: {
                    ...baseQuery,
                    [filterParam]: [filterValue.value],
                  },
                },
                { arrayFormat: 'bracket' },
              )
              const deselectSelfHref = includeParentFilter
                ? queryString.stringifyUrl(
                    {
                      url: location.pathname,
                      query: {
                        ...baseQuery,
                        [filterParam]:
                          appliedFilters[filterParam]?.filter(
                            (f) => f !== filterValue.value,
                          ) || [],
                      },
                    },
                    { arrayFormat: 'bracket' },
                  )
                : queryString.exclude(
                    `${location.pathname}${locationSearchString}`,
                    [filterParam],
                    {
                      arrayFormat: 'bracket',
                    },
                  )
              if (!isChecked) {
                if (nestedFilterHasValues) {
                  navigate(selectAllHref, { replace: true })
                  setActiveSubmenu(filterValue.nestedFilter.filterParam)
                } else {
                  navigate(selectSelfHref, { replace: true })
                  setActiveSubmenu(NONE_ID)
                }
              } else {
                if (nestedFilterHasValues) {
                  navigate(deselectAllHref, { replace: true })
                } else {
                  navigate(deselectSelfHref, { replace: true })
                }
                setActiveSubmenu(NONE_ID)
              }
              return
            }
            navigate(href, { replace: true })
          }}
          tabIndex={-1}
          requiredPermissions={[PERMISSIONS.READ]}
        />
        {filterValue.nestedFilter && nestedFilterHasValues && (
          <NestedFilterChevron
            isOpen={activeSubmenu == filterValue.nestedFilter?.filterParam}
          />
        )}
      </FilterDropdownItem>
      {!!filterValue.nestedFilter &&
        nestedFilterHasValues &&
        activeSubmenu == filterValue.nestedFilter.filterParam && (
          <NestedFilter
            filterParam={filterValue.nestedFilter.filterParam}
            filterValues={filterValue.nestedFilter.filterValues}
          />
        )}
    </FilterOptionContainer>
  )
}

export const MultiSelectDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  activeSubmenu,
  setActiveSubmenu,
  locationSearchString,
  includeParentFilter,
}: {
  filterParam: FilterParam
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string[]>
  activeSubmenu: string
  setActiveSubmenu: (value: string) => void
  locationSearchString: string
  includeParentFilter?: boolean
}): JSX.Element => (
  <>
    {filterValues.map((filterValue) => (
      <MultiSelectDropdownItemComponent
        key={filterValue.value}
        filterParam={filterParam}
        filterValue={filterValue}
        appliedFilters={appliedFilters}
        baseQuery={searchParams}
        location={location}
        activeSubmenu={activeSubmenu}
        setActiveSubmenu={setActiveSubmenu}
        locationSearchString={locationSearchString}
        includeParentFilter={includeParentFilter}
      />
    ))}
  </>
)

export const SingleSelectDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  onSelect,
}: {
  filterParam: FilterParam
  filterValue: DropdownItemType
  appliedFilters: Record<string, string>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  onSelect?: () => void
}): JSX.Element => {
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const selectedValue = appliedFilters[filterParam]
  const isSelected = selectedValue === filterValue.value
  const isCustom = isFunction(filterValue.customOnClick)

  // For single-select: if clicking the same item, deselect it (remove from URL)
  // Otherwise, select the new item
  const newValue = isSelected ? undefined : filterValue.value

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        ...(newValue ? { [filterParam]: newValue } : {}),
      },
    },
    { skipNull: true },
  )

  // Remove the param if deselecting
  const finalHref = isSelected
    ? queryString.exclude(
        `${location.pathname}${location.search}`,
        [filterParam],
        { arrayFormat: 'bracket' },
      )
    : href

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        to={finalHref}
        onClick={async (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(finalHref, { replace: true })
          }
          onSelect?.()
        }}
      >
        {filterValue.label}
      </FilterDropdownItem>
    </FilterOptionContainer>
  )
}

export const SingleSelectDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  onSelect,
}: {
  filterParam: FilterParam
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string>
  onSelect?: () => void
}): JSX.Element => (
  <>
    {filterValues.map((filterValue, index) => (
      <React.Fragment key={filterValue.value}>
        {filterValue.divider && index > 0 && <Dropdown.Divider />}
        <SingleSelectDropdownItemComponent
          filterParam={filterParam}
          filterValue={filterValue}
          appliedFilters={appliedFilters}
          baseQuery={searchParams}
          location={location}
          onSelect={onSelect}
        />
      </React.Fragment>
    ))}
  </>
)
